import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, AlertCircle, Loader2, X, Languages } from 'lucide-react'
import { showSuccess, showError } from '@/lib/swal'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { BranchSelection } from '@/components/shared/BranchSelection'
import { Logo } from '@/components/shared/Logo'
import { useAuthStore, useAuthHydrated, Branch } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { authService } from '@/services/authService'
import { apiClient } from '@/api/client'
import { setDocumentDirection, createRTLClassName } from '@/lib/rtl'

// Login form validation schema - will be initialized with translations
const createLoginSchema = (t: (key: string) => string) => z.object({
  email: z.string().email(t('auth.invalidEmail')),
  password: z.string().min(1, t('auth.passwordRequired')),
})

// Forget password validation schema - will be initialized with translations
const createForgetPasswordSchema = (t: (key: string) => string) => z.object({
  email: z.string().email(t('auth.invalidEmail')),
})

/**
 * Login page component with multi-step authentication
 * 1. Email/Password login
 * 2. User details validation (using token from step 1)
 * 3. Branch selection
 * Also handles branch switching flow
 */
export const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  
  // Create schemas with translations
  const loginSchema = createLoginSchema(t)
  const forgetPasswordSchema = createForgetPasswordSchema(t)
  
  type LoginFormData = z.infer<typeof loginSchema>
  type ForgetPasswordFormData = z.infer<typeof forgetPasswordSchema>
  const hydrated = useAuthHydrated()
  const { 
    isAuthenticated, 
    needsBranchSelection, 
    availableBranches,
    login, 
    selectBranch, 
    setLoading,
    user,
    token,
    isTokenExpired,
  } = useAuthStore()
  const { language, setLanguage } = useSettingsStore()
  
  const [showPassword, setShowPassword] = useState(false)
  const [, setCurrentStep] = useState<'login' | 'branch-selection'>('login')
  const [userInactive, setUserInactive] = useState(false)
  const [showForgetPasswordModal, setShowForgetPasswordModal] = useState(false)
  const [isForgetPasswordLoading, setIsForgetPasswordLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const {
    register: registerForgetPassword,
    handleSubmit: handleSubmitForgetPassword,
    formState: { errors: forgetPasswordErrors },
    reset: resetForgetPasswordForm,
  } = useForm<ForgetPasswordFormData>({
    resolver: zodResolver(forgetPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  // Note: Validation messages will automatically use the correct language
  // since schemas are recreated on each render with current translations

  // Language switch data and handler
  const languages = [
    {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      flag: '🇺🇸',
    },
    {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      flag: '🇸🇦',
    },
  ]

  const handleLanguageChange = async (langCode: string) => {
    try {
      // Update i18next language
      await i18n.changeLanguage(langCode)
      
      // Update settings store (this will also update document direction)
      setLanguage(langCode as 'en' | 'ar')
      
      // Ensure document direction is updated
      setDocumentDirection(langCode)
    } catch (error) {
      console.error('Failed to change language :', error)
    }
  }

  // Forget password handler
  const handleForgetPassword = async (data: ForgetPasswordFormData) => {
    try {
      setIsForgetPasswordLoading(true)
      
      console.log('🔐 Forget password request:', { email: data.email })
      
      const response = await apiClient.post('/api/ApiAuthentication/ForgetPassword', {
        email: data.email.toLowerCase().trim()
      })
      
      console.log('✅ Forget password response:', response.data)
      
      // Check response message
      if (response.data === "User not found") {
        await showError('User Not Found', 'Please insert correct email address.')
      } else if (response.data === "Password sent to your email.") {
        await showSuccess('Password Sent', 'Password has been sent to your Email.')
        setShowForgetPasswordModal(false)
        resetForgetPasswordForm()
      } else {
        // Default success message if response is different
        await showSuccess('Password Sent', 'Password has been sent to your Email.')
        setShowForgetPasswordModal(false)
        resetForgetPasswordForm()
      }
      
    } catch (error: any) {
      console.error('❌ Forget password error:', error)
      
      // Handle different error cases
      if (error.response?.status === 404) {
        await showError('User Not Found', 'Please insert correct email address.')
      } else if (error.response?.data?.message) {
        await showError('Error', error.response.data.message)
      } else if (error.response?.data === "User not found") {
        await showError('User Not Found', 'Please insert correct email address.')
      } else if (error.message) {
        await showError('Error', error.message)
      } else {
        await showError('Failed', 'Failed to send password reset email. Please try again.')
      }
    } finally {
      setIsForgetPasswordLoading(false)
    }
  }

  // Define handleBranchSelection BEFORE it's used
  const handleBranchSelection = async (branch: Branch) => {
    try {
      setLoading(true)
      
      console.log('🏢 Branch selected:', { 
        branchId: branch.branchId, 
        branchName: branch.branchName,
        roleId: branch.branchUserRoleId 
      })
      
      // Select the branch and complete authentication
      selectBranch(branch)
      
      await showSuccess('Branch Selected', `Selected ${branch.branchName}`)
      
      console.log('✅ Authentication complete! Redirecting to dashboard...')
      
      // Navigate to dashboard will happen automatically via router
    } catch (error) {
      console.error('❌ Branch selection error:', error)
      await showError('Failed', 'Failed to select branch')
    } finally {
      setLoading(false)
    }
  }

  // Redirect if already authenticated and has selected branch
  if (!hydrated) {
    return null
  }

  if (isAuthenticated && user?.selectedBranch && token && !isTokenExpired()) {
    return <Navigate to="/finance" replace />
  }

  // Show branch selection if needed (either from login or branch switching)
  if (needsBranchSelection && availableBranches.length > 0) {
    const isSwitchingBranch = user && !user.selectedBranch
    
    return (
      <div className="min-h-screen flex">
        {/* Left Side - Background Image with Overlay */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center" style={{backgroundImage: "url('/images/backgrounds/side.jpg')"}}>
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/50"></div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-secondary/30"></div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center px-12 py-20 text-white">
            <div className="max-w-md">
              <h1 className="text-5xl font-bold mb-6 leading-tight">
                {t('auth.welcomeTo')}<br />
                <span className="bg-gradient-to-r from-white to-secondary/80 bg-clip-text text-transparent">
                  {t('auth.erpSystem')}
                </span>
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed">
                {t('auth.erpDescription')}
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Branch Selection */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-white dark:bg-gray-900 lg:bg-gradient-to-br lg:from-gray-50 lg:to-gray-100 dark:lg:from-gray-900 dark:lg:to-gray-800 relative">
          {/* Language Switch - Top Right */}
          <div className="absolute top-6 right-6 z-20">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                    <Languages className="h-3 w-3 text-white" />
                  </div>
                  <span className="sr-only">{t('ui.langToggle')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={language === lang.code ? 'bg-accent' : ''}
                  >
                    <span className="mr-2 text-base">{lang.flag}</span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{lang.name}</span>
                      <span className="text-xs text-muted-foreground">{lang.nativeName}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="w-full max-w-md">
            {/* Branch Selection Form - No Background Box */}
            <div className="p-8 space-y-10 relative">
              <div className="relative z-10">
                {/* Header with Logo */}
                <div className="text-center space-y-6">
                  {/* Logo */}
                  <div className="flex justify-center mb-8 relative">
                    <div className="relative group">
                      <Logo 
                        alt="ERP Logo" 
                        className="w-[300px] h-auto object-contain transform group-hover:scale-105 transition-transform duration-300"
                      />
                      {/* Subtle glow effect behind logo */}
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h2 className="text-3xl font-black bg-gradient-to-r from-primary via-blue-700 to-secondary bg-clip-text text-transparent tracking-wide">
                      {isSwitchingBranch ? t('auth.switchBranch').toUpperCase() : t('auth.selectBranch').toUpperCase()}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 font-medium text-lg">
                      {isSwitchingBranch 
                        ? t('auth.chooseDifferentBranch')
                        : t('auth.chooseWorkspace')
                      }
                    </p>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto"></div>
                    
                    {isSwitchingBranch && user && (
                      <div className="mt-4 p-4 bg-blue-50/80 dark:bg-blue-900/20 rounded-2xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('auth.welcomeBack')}, <span className="font-bold text-primary">{user.userName}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Branch Selection */}
                <div className="mt-8">
                  <BranchSelection
                    branches={availableBranches}
                    onBranchSelect={handleBranchSelection}
                    isLoading={isSubmitting}
                    compact
                  />
                </div>

                {/* Footer */}
                <div className="text-center pt-6 mt-8 border-t border-gray-200/50 dark:border-gray-700/50">
                                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                {t('auth.secureAccess')}
              </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true)
      setUserInactive(false)

      // Convert email to lowercase before sending
      const loginData = {
        email: data.email.toLowerCase().trim(),
        password: data.password,
      }

      console.log('🔐 Step 1: Login attempt:', { email: loginData.email })

      // Step 1: Login to get token
      const loginResponse = await authService.login(loginData)

      console.log('✅ Step 1: Login successful:', { 
        userName: loginResponse.userName,
        tokenReceived: !!loginResponse.token.accessToken,
        expires: loginResponse.token.expires 
      })

      // Step 2: Get user details using the token from login
      console.log('👤 Step 2: Fetching user details with token...')
      const userDetails = await authService.getUserDetails(
        loginResponse.userName, 
        loginResponse.token.accessToken
      )

      console.log('✅ Step 2: User details retrieved:', { 
        isActive: userDetails.isActive,
        branchCount: userDetails.branches.length,
        branches: userDetails.branches.map(b => ({ id: b.branchId, name: b.branchName }))
      })

      // Step 3: Check if user is active
      if (!userDetails.isActive) {
        console.warn('⚠️ User account is inactive')
        setUserInactive(true)
        setLoading(false)
        return
      }

      // Step 4: Store login data and show branch selection
      console.log('🏪 Step 3: Preparing branch selection...')
      login(
        loginResponse.token.accessToken,
        loginResponse.token.expires,
        {
          userId: userDetails.userId,
          userName: userDetails.userName,
          email: userDetails.email,
          mobile: userDetails.mobile,
          isActive: userDetails.isActive,
          branches: userDetails.branches,
        }
      )

      await showSuccess('Login Successful', t('auth.loginSuccess'))
      setCurrentStep('branch-selection')
      
      console.log('✅ Authentication flow ready for branch selection')
    } catch (error: any) {
      console.error('❌ Login error:', error)
      
      // Handle specific error messages
      if (error.response?.status === 401) {
        await showError('Login Failed', 'Invalid email or password')
      } else if (error.response?.status === 404) {
        const isUserDetails = error.config?.url?.includes('UserDetailsByUsername')
        await showError(
          isUserDetails ? 'User details not found (404)' : 'Not Found',
          isUserDetails
            ? 'Login succeeded but user details API returned 404. Check .env VITE_API_URL (e.g. https://erpapi2026.erpaga.com) and backend path /api/ApiAuthentication/UserDetailsByUsername.'
            : 'User not found.'
        )
      } else if (error.response?.data?.message) {
        await showError('Login Failed', error.response.data.message)
      } else if (error.message) {
        await showError('Login Failed', error.message)
      } else {
        await showError('Login Failed', t('auth.loginError'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Illustration */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/images/backgrounds/side.jpg')"
          }}
        >
          {/* Dark overlay for better text visibility */}
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-secondary/30"></div>
        </div>
        
        {/* Welcome Content */}
        <div className="relative z-10 flex flex-col justify-center items-start p-12 text-white">
          <h1 className="text-4xl font-bold mb-6 leading-tight">
            {t('auth.welcomeTo')}<br />
            <span className="text-5xl font-black bg-gradient-to-r from-white to-secondary/80 bg-clip-text text-transparent">
              {t('auth.erpSystem')}
            </span>
          </h1>
          <p className="text-lg text-white/90 leading-relaxed max-w-md">
            {t('auth.erpDescription')}
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-white dark:bg-gray-900 lg:bg-gradient-to-br lg:from-gray-50 lg:to-gray-100 dark:lg:from-gray-900 dark:lg:to-gray-800 relative">
        {/* Language Switch - Top Right */}
        <div className="absolute top-6 right-6 z-20">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                  <Languages className="h-3 w-3 text-white" />
                </div>
                <span className="sr-only">{t('ui.langToggle')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={language === lang.code ? 'bg-accent' : ''}
                >
                  <span className="mr-2 text-base">{lang.flag}</span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{lang.name}</span>
                    <span className="text-xs text-muted-foreground">{lang.nativeName}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="w-full max-w-md">
          {/* Login Form - No Background Box */}
          <div className="p-8 space-y-10 relative">
            <div className="relative z-10">
            {/* Header with Logo */}
            <div className="text-center space-y-6">
              {/* Logo */}
              <div className="flex justify-center mb-8 relative">
                <div className="relative group">
                  <Logo 
                    alt="ERP Logo" 
                    className="w-[300px] h-auto object-contain transform group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Subtle glow effect behind logo */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
                </div>
              </div>
              
              <div className="space-y-3">
                <p className="text-gray-600 dark:text-gray-400 font-medium text-lg">
                  {t('auth.accessToERP')}
                </p>
              </div>
            </div>

            {/* User Inactive Warning */}
            {userInactive && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-2xl p-4 backdrop-blur-sm">
                <div className="flex items-center space-x-3 text-red-600 dark:text-red-400">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{t('auth.accountInactive')}</p>
                    <p className="text-sm opacity-90">
                      {t('auth.userNotActive')}
                    </p>
                  </div>
                </div>
              </div>
            )}



            {/* Login Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-3">
                <div className="relative group">
                  <div className={createRTLClassName({
                    common: "absolute top-1/2 transform -translate-y-1/2 z-10",
                    ltr: "left-4",
                    rtl: "right-4"
                  })}>
                    <div className="w-14 h-14 bg-gradient-to-tr from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center border border-primary/20 group-focus-within:scale-110 transition-transform duration-300">
                      <svg className="h-7 w-7 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                      </svg>
                    </div>
                  </div>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.enterUsername')}
                    {...register('email')}
                    className={createRTLClassName({
                      common: `h-16 bg-gray-50/90 dark:bg-gray-800/90 border-0 rounded-2xl transition-all duration-300 focus:ring-0 focus:border-0 focus:bg-white dark:focus:bg-gray-700 focus-visible:ring-0 focus-visible:border-0 focus-visible:outline-none shadow-lg hover:shadow-xl text-lg placeholder:text-gray-400 font-medium focus:outline-none ${errors.email ? 'border-red-300 dark:border-red-600' : ''}`,
                      ltr: "pl-24 pr-6",
                      rtl: "pr-24 pl-6"
                    })}
                    disabled={isSubmitting}
                  />
                  {/* Animated focus ring */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-3">
                <div className="relative group">
                  <div className={createRTLClassName({
                    common: "absolute top-1/2 transform -translate-y-1/2 z-10",
                    ltr: "left-4",
                    rtl: "right-4"
                  })}>
                    <div className="w-14 h-14 bg-gradient-to-tr from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center border border-primary/20 group-focus-within:scale-110 transition-transform duration-300">
                      <svg className="h-7 w-7 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  </div>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.enterPassword')}
                    {...register('password')}
                    className={createRTLClassName({
                      common: `h-16 bg-gray-50/90 dark:bg-gray-800/90 border-0 rounded-2xl transition-all duration-300 focus:ring-0 focus:border-0 focus:bg-white dark:focus:bg-gray-700 focus-visible:ring-0 focus-visible:border-0 focus-visible:outline-none shadow-lg hover:shadow-xl text-lg placeholder:text-gray-400 font-medium focus:outline-none ${errors.password ? 'border-red-300 dark:border-red-600' : ''}`,
                      ltr: "pl-24 pr-16",
                      rtl: "pr-24 pl-16"
                    })}
                    disabled={isSubmitting}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={createRTLClassName({
                      common: "absolute top-1/2 transform -translate-y-1/2 h-12 w-12 rounded-xl hover:bg-gray-100/80 dark:hover:bg-gray-700/80 transition-all duration-200",
                      ltr: "right-2",
                      rtl: "left-2"
                    })}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSubmitting}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-500" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-500" />
                    )}
                  </Button>
                  {/* Animated focus ring */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-16 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] hover:-translate-y-1 disabled:transform-none relative overflow-hidden group border border-primary/20"
                  disabled={isSubmitting}
                >
                  {/* Professional button glow effect matching dashboard */}
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  
                  <span className="relative z-10 flex items-center justify-center">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-3 h-6 w-6 animate-spin" />
{t('auth.signingIn')}
                      </>
                    ) : (
                      <>
                        <svg className="mr-3 h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
{t('auth.loginToERP')}
                      </>
                    )}
                  </span>
                </Button>
              </div>

              {/* Forget Password Link */}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setShowForgetPasswordModal(true)}
                  className="text-sm text-primary hover:text-secondary font-medium underline decoration-2 underline-offset-4 hover:decoration-secondary transition-all duration-300 hover:scale-105"
                  disabled={isSubmitting}
                >
{t('auth.forgotPassword')}
                </button>
              </div>
            </form>

            {/* Footer */}
            <div className="text-center pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('auth.secureAccess')}
              </p>
            </div>
            </div>
          </div>
        </div>

        {/* Forget Password Modal */}
        {showForgetPasswordModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6 space-y-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('auth.forgetPasswordTitle')}
                </h3>
                <button
                  onClick={() => {
                    setShowForgetPasswordModal(false)
                    resetForgetPasswordForm()
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  disabled={isForgetPasswordLoading}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {t('auth.enterEmailDescription')}
                </p>

                <form onSubmit={handleSubmitForgetPassword(handleForgetPassword)} className="space-y-4">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-gray-700 dark:text-gray-300 font-medium">
                      {t('auth.email')}
                    </Label>
                    <div className="relative">
                      <div className={createRTLClassName({
                        common: "absolute top-1/2 transform -translate-y-1/2 z-10",
                        ltr: "left-3",
                        rtl: "right-3"
                      })}>
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                          </svg>
                        </div>
                      </div>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder={t('auth.enterEmail')}
                        {...registerForgetPassword('email')}
                        className={createRTLClassName({
                          common: `h-12 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded-lg transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary ${forgetPasswordErrors.email ? 'border-red-300 dark:border-red-600' : ''}`,
                          ltr: "pl-14 pr-4",
                          rtl: "pr-14 pl-4"
                        })}
                        disabled={isForgetPasswordLoading}
                      />
                    </div>
                    {forgetPasswordErrors.email && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {forgetPasswordErrors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Modal Actions */}
                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForgetPasswordModal(false)
                        resetForgetPasswordForm()
                      }}
                      className="flex-1"
                      disabled={isForgetPasswordLoading}
                    >
                      {t('auth.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white"
                      disabled={isForgetPasswordLoading}
                    >
                      {isForgetPasswordLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('auth.sending')}
                        </>
                      ) : (
                        t('auth.sendPasswordToEmail')
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 